import { validateAddress } from '../geo/addressUtils';
import { isValidEmail } from '../deliveries/orderUploadUtils';
import { composePhone, isCompletePhone } from '../../utils/phone';

const REQUIRED = 'Campo obligatorio';

/** @param {*} value */
const isBlank = (value) => !String(value ?? '').trim();

/**
 * Checks one pickup window, `HH:MM` on both ends.
 *
 * **Half a window is an error, not a shortcut.** The backend would store the
 * one end and leave the other `NULL`, and Samex fills a missing end with its own
 * default — so a warehouse that said "from 09:00" would quietly end up
 * collecting until 18:00 because nobody typed the other half.
 *
 * An empty window is fine: that is a warehouse which does not collect in that
 * half of the day.
 *
 * @param {string} from
 * @param {string} to
 * @returns {{from?: string, to?: string}} Errors keyed by which end is wrong.
 */
export const validatePickupWindow = (from, to) => {
  const hasFrom = !isBlank(from);
  const hasTo = !isBlank(to);

  if (!hasFrom && !hasTo) return {};
  if (hasFrom && !hasTo) return { to: 'Falta la hora de término' };
  if (!hasFrom && hasTo) return { from: 'Falta la hora de inicio' };

  // `HH:MM` from `<input type="time">` compares correctly as a string.
  if (from >= to) return { to: 'Debe ser posterior al inicio' };

  return {};
};

/** `"09:00:00"` from the backend, `"09:00"` for the form. */
export const toTimeInput = (value) => String(value || '').slice(0, 5);

/* -------------------------------------------------------------------------- */
/*                               Pickup schedule                              */
/* -------------------------------------------------------------------------- */

/**
 * How a warehouse is collected. The backend stores two windows and a flag; the
 * form asks the question that actually has an answer — whether the day is cut
 * in half — and derives the rest.
 */
export const PICKUP_MODES = {
  ALL_DAY: 'all_day',
  SINGLE: 'single',
  SPLIT: 'split',
};

export const PICKUP_MODE_OPTIONS = [
  {
    value: PICKUP_MODES.ALL_DAY,
    label: 'Todo el día',
    description: 'El courier puede pasar a cualquier hora.',
  },
  {
    value: PICKUP_MODES.SINGLE,
    label: 'Un solo tramo',
    description: 'Una franja continua, sin corte al mediodía.',
  },
  {
    value: PICKUP_MODES.SPLIT,
    label: 'Mañana y tarde',
    description: 'Dos franjas, con un corte entre medio.',
  },
];

/** Every half hour of the day, which is as fine as a courier's route is planned. */
export const PICKUP_TIME_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const time = `${String(Math.floor(index / 2)).padStart(2, '0')}:${index % 2 === 0 ? '00' : '30'}`;
  return { value: time, label: time };
});

/** What each mode starts with when the warehouse has nothing of its own. */
export const DEFAULT_PICKUP_WINDOWS = {
  [PICKUP_MODES.SINGLE]: { amFrom: '09:00', amTo: '18:00', pmFrom: '', pmTo: '' },
  [PICKUP_MODES.SPLIT]: { amFrom: '09:00', amTo: '13:00', pmFrom: '15:00', pmTo: '18:00' },
};

/**
 * The mode a set of values means. Derived rather than stored: an afternoon
 * window is what makes a day split, so the two can never disagree.
 */
export const pickupModeFromValues = ({ allDay, pmFrom, pmTo } = {}) => {
  if (allDay) return PICKUP_MODES.ALL_DAY;
  if (!isBlank(pmFrom) || !isBlank(pmTo)) return PICKUP_MODES.SPLIT;
  return PICKUP_MODES.SINGLE;
};

/**
 * The patch that switches to a mode, keeping whatever hours were already
 * chosen and filling the rest with the usual ones.
 */
export const applyPickupMode = (mode, values = {}) => {
  if (mode === PICKUP_MODES.ALL_DAY) return { allDay: true };

  const defaults = DEFAULT_PICKUP_WINDOWS[mode] || DEFAULT_PICKUP_WINDOWS[PICKUP_MODES.SINGLE];
  const wasSplit = !isBlank(values.pmFrom) || !isBlank(values.pmTo);
  const amFrom = values.amFrom || defaults.amFrom;

  // Joining two stretches into one keeps the day's ends: it starts where the
  // morning started and finishes where the afternoon finished.
  if (mode === PICKUP_MODES.SINGLE) {
    return {
      allDay: false,
      amFrom,
      amTo: (wasSplit ? values.pmTo : values.amTo) || defaults.amTo,
      pmFrom: '',
      pmTo: '',
    };
  }

  // Cutting one stretch in two cannot keep its end as the morning's, or the
  // morning would run into the afternoon. Only the start survives.
  if (!wasSplit) {
    return { allDay: false, amFrom, amTo: defaults.amTo, pmFrom: defaults.pmFrom, pmTo: defaults.pmTo };
  }

  return {
    allDay: false,
    amFrom,
    amTo: values.amTo || defaults.amTo,
    pmFrom: values.pmFrom || defaults.pmFrom,
    pmTo: values.pmTo || defaults.pmTo,
  };
};

/**
 * The form of a stored warehouse.
 *
 * A warehouse with nothing stored is already being collected on the courier's
 * own default, so the form opens on that same stretch: saving turns a default
 * nobody chose into one somebody did.
 *
 * @param {object} warehouse - As the backend answers it.
 */
export const pickupFormFromWarehouse = (warehouse) => {
  const values = {
    allDay: Boolean(warehouse?.all_day_pickup),
    amFrom: toTimeInput(warehouse?.am_pickup_from),
    amTo: toTimeInput(warehouse?.am_pickup_to),
    pmFrom: toTimeInput(warehouse?.pm_pickup_from),
    pmTo: toTimeInput(warehouse?.pm_pickup_to),
  };

  const hasWindows = [values.amFrom, values.amTo, values.pmFrom, values.pmTo].some((time) => !isBlank(time));
  if (!values.allDay && !hasWindows) {
    return { allDay: false, ...DEFAULT_PICKUP_WINDOWS[PICKUP_MODES.SINGLE] };
  }

  return values;
};

/**
 * Errors of a schedule, one message per window.
 *
 * @returns {{am?: string, pm?: string}}
 */
export const validatePickupSchedule = (values = {}) => {
  const mode = pickupModeFromValues(values);
  if (mode === PICKUP_MODES.ALL_DAY) return {};

  const errors = {};
  const am = validatePickupWindow(values.amFrom, values.amTo);
  if (am.from || am.to) errors.am = am.from || am.to;

  if (mode === PICKUP_MODES.SPLIT) {
    const pm = validatePickupWindow(values.pmFrom, values.pmTo);
    if (pm.from || pm.to) errors.pm = pm.from || pm.to;

    // Two stretches that overlap are one stretch, and the courier would read
    // them as such.
    if (!errors.am && !errors.pm && values.pmFrom <= values.amTo) {
      errors.pm = 'La tarde debe empezar después de que termina la mañana';
    }
  }

  return errors;
};

/** The keys the seller wizard's warehouse form uses for the same five values. */
const WAREHOUSE_PICKUP_KEYS = {
  allDay: 'allDayPickup',
  amFrom: 'amPickupFrom',
  amTo: 'amPickupTo',
  pmFrom: 'pmPickupFrom',
  pmTo: 'pmPickupTo',
};

/** A `PickupScheduleFields` patch, renamed for the wizard's warehouse card. */
export const toWarehousePickupPatch = (patch = {}) =>
  Object.fromEntries(
    Object.entries(patch).map(([key, value]) => [WAREHOUSE_PICKUP_KEYS[key] || key, value])
  );

/** The choice in a sentence, so the form says what it is about to save. */
export const describePickupChoice = (values = {}) => {
  const mode = pickupModeFromValues(values);

  if (mode === PICKUP_MODES.ALL_DAY) return 'El courier puede pasar a cualquier hora del día.';
  if (mode === PICKUP_MODES.SPLIT) {
    return `El courier puede pasar entre las ${values.amFrom} y las ${values.amTo}, y entre las ${values.pmFrom} y las ${values.pmTo}.`;
  }
  return `El courier puede pasar entre las ${values.amFrom} y las ${values.amTo}.`;
};

/**
 * The pickup hours of a stored warehouse, in one line.
 *
 * "Sin horario" is not cosmetic: Samex fills an empty schedule with 09:00–18:00
 * of its own, so a warehouse showing it is collecting on hours nobody chose.
 *
 * @param {object} warehouse - As the backend answers it.
 * @returns {string}
 */
export const describePickupSchedule = (warehouse) => {
  if (warehouse?.all_day_pickup) return 'Todo el día';

  const windows = [
    [warehouse?.am_pickup_from, warehouse?.am_pickup_to],
    [warehouse?.pm_pickup_from, warehouse?.pm_pickup_to],
  ]
    .filter(([from, to]) => from && to)
    .map(([from, to]) => `${toTimeInput(from)}–${toTimeInput(to)}`);

  return windows.length > 0 ? windows.join(' · ') : 'Sin horario';
};

/**
 * What `update_pickup_schedule` expects. Every field travels, empty included:
 * the backend turns an empty string into `NULL`, which is how turning "all day"
 * on clears the windows that were there.
 *
 * @param {{allDay: boolean, amFrom: string, amTo: string, pmFrom: string, pmTo: string}} form
 */
export const buildPickupSchedulePayload = ({ allDay, amFrom, amTo, pmFrom, pmTo }) => ({
  all_day_pickup: Boolean(allDay),
  am_pickup_from: allDay ? '' : (amFrom || ''),
  am_pickup_to: allDay ? '' : (amTo || ''),
  pm_pickup_from: allDay ? '' : (pmFrom || ''),
  pm_pickup_to: allDay ? '' : (pmTo || ''),
});

/**
 * Whether a validator's result means "valid".
 *
 * Aware of the warehouse case, where the value under a key is a sparse array of
 * per-card error maps: an array of empty slots still means no errors.
 *
 * @param {object} errors
 * @returns {boolean}
 */
export const isValid = (errors) =>
  Object.values(errors || {}).every((value) =>
    Array.isArray(value)
      ? value.every((entry) => !entry || Object.keys(entry).length === 0)
      : !value
  );

/* -------------------------------------------------------------------------- */
/*                              Per-step validation                           */
/* -------------------------------------------------------------------------- */

export const validateCompanyStep = (form) => {
  const errors = {};

  if (isBlank(form.razonSocial)) errors.razonSocial = REQUIRED;
  if (isBlank(form.taxId)) errors.taxId = REQUIRED;
  if (isBlank(form.plan)) errors.plan = REQUIRED;

  return errors;
};

/**
 * The email is valid only once the server has confirmed it is free.
 *
 * An unknown answer — the lookup failed — is let through: the create call
 * checks it again, and a network hiccup should not trap the operator on this
 * step. A pending answer is not an error either; the step blocks on it through
 * `isAccountStepBlocked` instead, so no red text flashes while typing.
 *
 * @param {object} form
 * @param {{email: string, status: string, available: boolean|null}} emailCheck
 */
export const validateAccountStep = (form, emailCheck) => {
  const errors = {};
  const email = String(form.email || '').trim();

  if (!email) {
    errors.email = REQUIRED;
    return errors;
  }

  if (!isValidEmail(email)) {
    errors.email = 'Correo inválido';
    return errors;
  }

  if (emailCheck?.email === email && emailCheck.status === 'done' && emailCheck.available === false) {
    errors.email = 'Este correo ya está registrado en la plataforma';
  }

  return errors;
};

/**
 * Whether the account step must hold, waiting on the availability lookup.
 *
 * Separate from the errors so the wait reads as a pending state rather than as
 * a mistake, and so an address that has not been looked up yet cannot slip
 * through by clicking faster than the debounce.
 */
export const isAccountStepBlocked = (form, emailCheck) => {
  const email = String(form.email || '').trim();
  if (!email || !isValidEmail(email)) return false;
  return emailCheck?.email !== email || emailCheck.status === 'checking';
};

/** @param {{isMexico: boolean}} options */
export const validateAddressStep = (form, { isMexico }) => validateAddress(form, { isMexico });

/**
 * Validates every warehouse card, returning a sparse array indexed the same way
 * as `form.warehouses` so each error lands on the card that caused it.
 *
 * @param {{isMexico: boolean}} options
 */
export const validateWarehousesStep = (form, { isMexico }) => {
  const warehouseErrors = (form.warehouses || []).map((warehouse) => {
    const errors = validateAddress(warehouse, { isMexico });

    if (isBlank(warehouse.name)) errors.name = REQUIRED;
    if (isBlank(warehouse.storeKeeper)) errors.storeKeeper = REQUIRED;
    if (isBlank(warehouse.shippingZone)) errors.shippingZone = REQUIRED;
    if (isBlank(warehouse.shippingMethod)) errors.shippingMethod = REQUIRED;

    // Required by the backend even though the legacy form treated it as
    // optional, which is why leaving it blank there always failed the create.
    if (isBlank(warehouse.customerPhone)) {
      errors.customerPhone = REQUIRED;
    } else if (!isCompletePhone(warehouse.customerPhone, warehouse.customerPhoneCountry)) {
      errors.customerPhone = 'Número incompleto';
    }

    // Optional, but a half-typed number is still worth catching.
    if (!isBlank(warehouse.phone) && !isCompletePhone(warehouse.phone, warehouse.phoneCountry)) {
      errors.phone = 'Número incompleto';
    }

    if (!isBlank(warehouse.email) && !isValidEmail(String(warehouse.email).trim())) {
      errors.email = 'Correo inválido';
    }

    if (!isBlank(warehouse.capacity) && Number(warehouse.capacity) <= 0) {
      errors.capacity = 'Debe ser mayor a 0';
    }

    const schedule = validatePickupSchedule({
      allDay: warehouse.allDayPickup,
      amFrom: warehouse.amPickupFrom,
      amTo: warehouse.amPickupTo,
      pmFrom: warehouse.pmPickupFrom,
      pmTo: warehouse.pmPickupTo,
    });
    if (schedule.am) errors.amPickupWindow = schedule.am;
    if (schedule.pm) errors.pmPickupWindow = schedule.pm;

    return errors;
  });

  const hasAny = warehouseErrors.some((errors) => Object.keys(errors).length > 0);
  return hasAny ? { warehouses: warehouseErrors } : {};
};

/**
 * Runs every step, for the summary and for the step indicator's red marks.
 *
 * @returns {Object.<string, object>} Errors by step id
 */
export const validateAllSteps = (form, { emailCheck, isMexico }) => ({
  company: validateCompanyStep(form),
  account: validateAccountStep(form, emailCheck),
  address: validateAddressStep(form, { isMexico }),
  warehouses: validateWarehousesStep(form, { isMexico }),
});

/* -------------------------------------------------------------------------- */
/*                               Payload building                             */
/* -------------------------------------------------------------------------- */

/**
 * Maps the address half of a form section onto the field names the endpoint
 * uses. `settlement` and `postal_code` only travel when they were filled in:
 * the backend decides whether they are required, from the country of the
 * address, and Chile has no use for either.
 */
const buildAddressPayload = (source) => {
  const payload = {
    region: source.region,
    comuna: source.commune,
    street: String(source.street || '').trim(),
    number: String(source.streetNumber || '').trim(),
  };

  if (source.settlement) payload.settlement = source.settlement;
  if (source.postalCode) payload.postal_code = source.postalCode;

  return payload;
};

/**
 * Turns the wizard state into the create-seller request body.
 *
 * @param {object} form
 * @param {{adminId: number|string}} options
 * @returns {object}
 */
export const buildCreateSellerPayload = (form, { adminId }) => ({
  admin: adminId,
  user: String(form.email || '').trim(),
  razon_social: String(form.razonSocial || '').trim(),
  rut: String(form.taxId || '').trim(),
  plan: form.plan,
  ...buildAddressPayload(form),
  warehouses: (form.warehouses || []).map((warehouse) => {
    const payload = {
      name: String(warehouse.name || '').trim(),
      store_keeper: String(warehouse.storeKeeper || '').trim(),
      phone: composePhone(warehouse.phoneCountry, warehouse.phone),
      email: String(warehouse.email || '').trim(),
      customer_phone: composePhone(warehouse.customerPhoneCountry, warehouse.customerPhone),
      shipping_zone: warehouse.shippingZone,
      shipping_method: warehouse.shippingMethod,
      warehouse_comments: String(warehouse.comments || '').trim(),
      ...buildAddressPayload(warehouse),
    };

    // Left out when blank so the backend applies its own default rather than
    // receiving an empty string.
    if (String(warehouse.capacity ?? '').trim()) {
      payload.capacity = Number(warehouse.capacity);
    }

    payload.all_day_pickup = Boolean(warehouse.allDayPickup);

    if (!warehouse.allDayPickup) {
      const windows = {
        am_pickup_from: warehouse.amPickupFrom,
        am_pickup_to: warehouse.amPickupTo,
        pm_pickup_from: warehouse.pmPickupFrom,
        pm_pickup_to: warehouse.pmPickupTo,
      };

      Object.entries(windows).forEach(([key, value]) => {
        const time = String(value ?? '').trim();
        if (time) payload[key] = time;
      });
    }

    return payload;
  }),
});
